import React, { useState, useEffect } from 'react';
import './QuoteModal.css';

function QuoteModal({ product, onClose, onSubmit }) {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    vehicleMake: '',
    vehicleModel: '',
    yearOfManufacture: '',
    vehicleValue: '',
    coverType: '', // 'comprehensive' or 'tpo'
    capacity: '',
    tonnage: '',
    excessProtector: false,
    pvt: false,
    paymentOption: '', // 'annual', 'installments2', 'installments3'
  });
  const [premiumBreakdown, setPremiumBreakdown] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);

  // Product categories
  const isPSVProduct = product.category === 'psv' || product.category === 'school';
  const isCommercialProduct = product.category === 'commercial';

  // Calculate premium whenever relevant fields change
  useEffect(() => {
    if (formData.coverType && formData.vehicleValue) {
      calculatePremium();
    }
  }, [formData.coverType, formData.vehicleValue, formData.capacity, formData.tonnage, formData.excessProtector, formData.pvt, formData.paymentOption]);

  const calculatePremium = async () => {
    setIsCalculating(true);
    try {
      if (formData.coverType === 'tpo') {
        // Call API for TPO
        try {
          const response = await fetch('https://digitalapi.definiteassurance.com/api/calculatePremium2', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              sumInsured: 0,
              capacity: isPSVProduct ? (parseInt(formData.capacity) || 0) : 0,
              tonnage: isCommercialProduct ? (parseFloat(formData.tonnage) || 0) : 0,
              coverPeriod: 'Annually',
              product: product.productCode,
              coverOption: product.coverOption,
            }),
          });
          
          if (!response.ok) {
            throw new Error('API request failed');
          }
          
          const data = await response.json();
          console.log('TPO API Response:', data);
          
          const basePremium = parseFloat(data.premium) || 0;
          const levies = basePremium * 0.0045;
          const stampDuty = 40;
          const totalPremium = basePremium + levies + stampDuty;
          
          // Calculate installment amounts - divide only premium, then add levies and stamp duty
          let installmentAmount = totalPremium;
          if (formData.paymentOption === 'installments2') {
            installmentAmount = (basePremium / 2) + levies + stampDuty;
          }
          
          setPremiumBreakdown({
            basePremium,
            excessProtectorPremium: 0,
            pvtPremium: 0,
            levies,
            stampDuty,
            totalPremium,
            installmentAmount: formData.paymentOption === 'installments2' ? installmentAmount : totalPremium,
            numberOfInstallments: formData.paymentOption === 'installments2' ? 2 : 1,
          });
        } catch (apiError) {
          console.error('TPO API Error:', apiError);
          // If API fails, set null to show error state
          setPremiumBreakdown(null);
        }
      } else {
        // Use existing xlsx-based calculation for comprehensive
        // Get the correct rate based on product type
        const rateMap = {
          'Motor Private': 0.03,
          'PSV Chauffeur Driven': 0.045,
          'Motor Commercial Own Goods': 0.04,
          'Motor Commercial General Cartage': 0.045,
          'Tuk Tuk Commercial': 0.03,
          'Tuk Tuk PSV': 0.04,
          'PSV Self Drive': 0.045,
          'School Bus': 0.05,
          'PSV Matatu': 0.05,
          'PSV Bus': 0.045,
          'Motorcycle Private': 0.03,
          'Motor Cycle BodaBoda': 0.04,
          'Motor Commercial Institutional': 0.05,
          'Motor Commercial Agricultural': 0.04,
        };
        
        const rate = rateMap[product.name] || 0.03;
        const basePremium = parseFloat(formData.vehicleValue) * rate;
        let excessProtectorPremium = 0;
        let pvtPremium = 0;
        
        if (formData.excessProtector) {
          excessProtectorPremium = basePremium * 0.0025;
        }
        
        if (formData.pvt) {
          pvtPremium = basePremium * 0.0045;
        }
        
        const levies = basePremium * 0.0045;
        const stampDuty = 40;
        const totalPremium = basePremium + excessProtectorPremium + pvtPremium + levies + stampDuty;
        // Calculate installment amounts - divide only premium and benefits, then add levies and stamp duty
        let installmentAmount = totalPremium;
        if (formData.paymentOption === 'installments3') {
          installmentAmount = ((basePremium + excessProtectorPremium + pvtPremium) / 3) + levies + stampDuty;
        }
        
        setPremiumBreakdown({
          basePremium,
          excessProtectorPremium,
          pvtPremium,
          levies,
          stampDuty,
          totalPremium,
          installmentAmount: formData.paymentOption === 'installments3' ? installmentAmount : totalPremium,
          numberOfInstallments: formData.paymentOption === 'installments3' ? 3 : 1,
        });
      }
    } catch (error) {
      console.error('Error calculating premium:', error);
    }
    setIsCalculating(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsGenerating(true);
    await onSubmit({ ...formData, premiumBreakdown });
    setIsGenerating(false);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name === 'vehicleValue') {
      // Remove all non-digit characters
      const numericValue = value.replace(/[^0-9]/g, '');
      // Format with commas
      const formattedValue = numericValue ? parseInt(numericValue).toLocaleString() : '';
      setFormData(prev => ({
        ...prev,
        [name]: numericValue // Store raw number
      }));
      // Update the display value
      e.target.value = formattedValue;
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const handleNext = (e) => {
    e.preventDefault();
    if (step < 4) {
      setStep(step + 1);
    }
  };

  const handlePrevious = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  const handleDownloadQuote = async () => {
    setIsGenerating(true);
    await onSubmit({ ...formData, action: 'download', premiumBreakdown });
    setIsGenerating(false);
  };

  const handleBuyCover = async () => {
    setIsGenerating(true);
    await onSubmit({ ...formData, action: 'buy', premiumBreakdown });
    setIsGenerating(false);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content-wrapper" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>&times;</button>
        
        <div className="modal-grid">
          {/* Left Panel - Premium Breakdown */}
          <div className="premium-panel">
            <h3>Premium Breakdown</h3>
            {premiumBreakdown ? (
              <div className="premium-details">
                <div className="premium-row">
                  <span>Base Premium:</span>
                  <span>KES {Math.round(premiumBreakdown.basePremium).toLocaleString()}</span>
                </div>
                {formData.coverType === 'comprehensive' && formData.excessProtector && (
                  <div className="premium-row">
                    <span>Excess Protector:</span>
                    <span>KES {Math.round(premiumBreakdown.excessProtectorPremium).toLocaleString()}</span>
                  </div>
                )}
                {formData.coverType === 'comprehensive' && formData.pvt && (
                  <div className="premium-row">
                    <span>PVT:</span>
                    <span>KES {Math.round(premiumBreakdown.pvtPremium).toLocaleString()}</span>
                  </div>
                )}
                <div className="premium-row">
                  <span>Levies (0.45%):</span>
                  <span>KES {Math.round(premiumBreakdown.levies).toLocaleString()}</span>
                </div>
                <div className="premium-row">
                  <span>Stamp Duty:</span>
                  <span>KES {premiumBreakdown.stampDuty.toLocaleString()}</span>
                </div>
                <div className="premium-row total">
                  <span>{premiumBreakdown.numberOfInstallments > 1 ? `Installment Premium (${premiumBreakdown.numberOfInstallments}x):` : 'Total Premium:'}</span>
                  <span>KES {Math.round(premiumBreakdown.installmentAmount).toLocaleString()}</span>
                </div>
              </div>
            ) : (
              <p className="no-premium">Complete the form to see premium breakdown</p>
            )}
            
            <div className="action-buttons">
              <button 
                className="btn-download" 
                onClick={handleDownloadQuote}
                disabled={!premiumBreakdown || isGenerating}
              >
                {isGenerating ? 'Processing...' : 'Download Quote'}
              </button>
              <button 
                className="btn-buy" 
                onClick={handleBuyCover}
                disabled={!premiumBreakdown || isGenerating}
              >
                Buy Cover
              </button>
            </div>
          </div>

          {/* Right Panel - Form Steps */}
          <div className="form-panel">
            <div className="modal-header">
              <h2>{product.name}</h2>
              <div className="step-indicator">
                <span className={step >= 1 ? 'active' : ''}>1</span>
                <span className={step >= 2 ? 'active' : ''}>2</span>
                <span className={step >= 3 ? 'active' : ''}>3</span>
                <span className={step >= 4 ? 'active' : ''}>4</span>
              </div>
            </div>

            <form onSubmit={step === 4 ? handleSubmit : handleNext} className="quote-form">
              {/* Step 1: Personal & Vehicle Details */}
              {step === 1 && (
                <div className="form-step">
                  <h3>Personal & Vehicle Details</h3>
                  
                  {/* Personal Details Section */}
                  <div className="section-header">
                    <span className="section-title">Personal Information</span>
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="fullName">
                      Full Name <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      id="fullName"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleChange}
                      placeholder="Enter your full name"
                      required
                      className="form-input"
                    />
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="email">
                        Email <span className="required">*</span>
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="your.email@example.com"
                        required
                        className="form-input"
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="phone">
                        Phone Number <span className="required">*</span>
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="0712345678"
                        required
                        className="form-input"
                      />
                    </div>
                  </div>

                  {/* Vehicle Details Section */}
                  <div className="section-separator"></div>
                  <div className="section-header">
                    <span className="section-title">Vehicle Information</span>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="vehicleMake">
                        Vehicle Make <span className="required">*</span>
                      </label>
                      <input
                        type="text"
                        id="vehicleMake"
                        name="vehicleMake"
                        value={formData.vehicleMake}
                        onChange={handleChange}
                        placeholder="e.g., Toyota"
                        required
                        className="form-input"
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="vehicleModel">
                        Vehicle Model <span className="required">*</span>
                      </label>
                      <input
                        type="text"
                        id="vehicleModel"
                        name="vehicleModel"
                        value={formData.vehicleModel}
                        onChange={handleChange}
                        placeholder="e.g., Corolla"
                        required
                        className="form-input"
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="yearOfManufacture">
                        Year of Manufacture <span className="required">*</span>
                      </label>
                      <input
                        type="number"
                        id="yearOfManufacture"
                        name="yearOfManufacture"
                        value={formData.yearOfManufacture}
                        onChange={handleChange}
                        placeholder="e.g., 2020"
                        required
                        min="1900"
                        max={new Date().getFullYear() + 1}
                        className="form-input"
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="vehicleValue">
                        Vehicle Value (KES) <span className="required">*</span>
                      </label>
                      <input
                        type="text"
                        id="vehicleValue"
                        name="vehicleValue"
                        value={formData.vehicleValue ? parseInt(formData.vehicleValue).toLocaleString() : ''}
                        onChange={handleChange}
                        placeholder="e.g., 1,000,000"
                        required
                        className="form-input"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Step 2: Cover Selection */}
              {step === 2 && (
                <div className="form-step">
                  <h3>Select Cover Type</h3>
                  
                  <div className="cover-options">
                    <label className={`cover-card ${formData.coverType === 'comprehensive' ? 'selected' : ''}`}>
                      <input
                        type="radio"
                        name="coverType"
                        value="comprehensive"
                        checked={formData.coverType === 'comprehensive'}
                        onChange={handleChange}
                        required
                      />
                      <div className="cover-card-content">
                        <h4>Comprehensive</h4>
                        <p>Full coverage including theft and damage</p>
                      </div>
                    </label>

                    <label className={`cover-card ${formData.coverType === 'tpo' ? 'selected' : ''}`}>
                      <input
                        type="radio"
                        name="coverType"
                        value="tpo"
                        checked={formData.coverType === 'tpo'}
                        onChange={handleChange}
                        required
                      />
                      <div className="cover-card-content">
                        <h4>Third Party Only (TPO)</h4>
                        <p>Basic legal requirement coverage</p>
                      </div>
                    </label>
                  </div>

                  {isPSVProduct && (
                    <div className="form-group">
                      <label htmlFor="capacity">
                        Passenger Capacity <span className="required">*</span>
                      </label>
                      <input
                        type="number"
                        id="capacity"
                        name="capacity"
                        value={formData.capacity}
                        onChange={handleChange}
                        placeholder="e.g., 14"
                        required
                        min="1"
                        className="form-input"
                      />
                    </div>
                  )}

                  {isCommercialProduct && (
                    <div className="form-group">
                      <label htmlFor="tonnage">
                        Tonnage <span className="required">*</span>
                      </label>
                      <input
                        type="number"
                        id="tonnage"
                        name="tonnage"
                        value={formData.tonnage}
                        onChange={handleChange}
                        placeholder="e.g., 5.0"
                        required
                        min="0"
                        step="0.1"
                        className="form-input"
                      />
                    </div>
                  )}
                </div>
              )}

              {/* Step 3: Additional Benefits */}
              {step === 3 && (
                <div className="form-step">
                  <h3>Additional Benefits</h3>
                  
                  {formData.coverType === 'comprehensive' ? (
                    <div className="benefits-section">
                      <div className="form-group checkbox-group">
                        <label className="checkbox-label">
                          <input
                            type="checkbox"
                            name="pvt"
                            checked={formData.pvt}
                            onChange={handleChange}
                            className="form-checkbox"
                          />
                          <span className="checkbox-text">
                            <strong>Political Violence & Terrorism (PVT)</strong>
                            <small>Coverage against political violence and terrorism - 0.45% of premium</small>
                          </span>
                        </label>
                      </div>

                      <div className="form-group checkbox-group">
                        <label className="checkbox-label">
                          <input
                            type="checkbox"
                            name="excessProtector"
                            checked={formData.excessProtector}
                            onChange={handleChange}
                            className="form-checkbox"
                          />
                          <span className="checkbox-text">
                            <strong>Excess Protector</strong>
                            <small>Protects you from paying excess on claims - 0.25% of premium</small>
                          </span>
                        </label>
                      </div>
                    </div>
                  ) : (
                    <p className="no-benefits">No additional benefits available for Third Party Only cover.</p>
                  )}
                </div>
              )}

              {/* Step 4: Payment Options */}
              {step === 4 && (
                <div className="form-step">
                  <h3>Payment Options</h3>
                  
                  <div className="payment-options">
                    <label className={`payment-card ${formData.paymentOption === 'annual' ? 'selected' : ''}`}>
                      <input
                        type="radio"
                        name="paymentOption"
                        value="annual"
                        checked={formData.paymentOption === 'annual'}
                        onChange={handleChange}
                        required
                      />
                      <div className="payment-card-content">
                        <h4>Annual Payment</h4>
                        <p>Pay the full premium at once</p>
                      </div>
                    </label>

                    {formData.coverType === 'comprehensive' ? (
                      <label className={`payment-card ${formData.paymentOption === 'installments3' ? 'selected' : ''}`}>
                        <input
                          type="radio"
                          name="paymentOption"
                          value="installments3"
                          checked={formData.paymentOption === 'installments3'}
                          onChange={handleChange}
                          required
                        />
                        <div className="payment-card-content">
                          <h4>3 Installments</h4>
                          <p>Pay in three equal installments</p>
                        </div>
                      </label>
                    ) : (
                      <label className={`payment-card ${formData.paymentOption === 'installments2' ? 'selected' : ''}`}>
                        <input
                          type="radio"
                          name="paymentOption"
                          value="installments2"
                          checked={formData.paymentOption === 'installments2'}
                          onChange={handleChange}
                          required
                        />
                        <div className="payment-card-content">
                          <h4>2 Installments</h4>
                          <p>Pay in two equal installments</p>
                        </div>
                      </label>
                    )}
                  </div>
                </div>
              )}

              <div className="form-navigation">
                {step > 1 && (
                  <button type="button" onClick={handlePrevious} className="btn-secondary">
                    Previous
                  </button>
                )}
                <button type="submit" className="btn-primary">
                  {step === 4 ? 'Complete' : 'Next'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

export default QuoteModal;
